// Aucun jeton stocke : la session est un cookie HttpOnly illisible ici. Seul le
// jeton antiforgery est detenu, inutile sans le cookie.

import { trackSession } from './sessionClock'

const CSRF_HEADER = 'X-CSRF-TOKEN'

export class ApiError extends Error {
  readonly status: number
  readonly payload: unknown
  readonly path: string

  constructor(message: string, status: number, payload: unknown, path = '') {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
    this.path = path
  }
}

let csrfToken: string | null = null

export function resetCsrfToken(): void {
  csrfToken = null
}

async function ensureCsrfToken(): Promise<string> {
  if (csrfToken !== null) {
    return csrfToken
  }

  const response = await fetch('/auth/csrf', { credentials: 'same-origin' })
  trackSession(response)
  if (!response.ok) {
    throw new ApiError(
      "Impossible d'obtenir un jeton antiforgery.",
      response.status,
      null,
    )
  }

  const body = (await response.json()) as { token: string }
  csrfToken = body.token
  return body.token
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (text.length === 0) {
    return null
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

function readString(payload: unknown, key: string): string | null {
  if (typeof payload !== 'object' || payload === null) {
    return null
  }

  const value = (payload as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : null
}

// Les regles de mot de passe non respectees sont la, pas dans le titre.
function readValidationErrors(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) {
    return null
  }

  const errors = (payload as Record<string, unknown>).errors
  if (typeof errors !== 'object' || errors === null) {
    return null
  }

  const messages = Object.values(errors as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : []))
    .filter((value): value is string => typeof value === 'string')

  return messages.length > 0 ? messages.join(' ') : null
}

export function errorCode(error: unknown): string | null {
  return error instanceof ApiError ? readString(error.payload, 'code') : null
}

function messageFor(status: number, payload: unknown): string {
  const validation = readValidationErrors(payload)
  if (validation !== null) {
    return validation
  }

  const fromBody = readString(payload, 'message') ?? readString(payload, 'title')
  if (fromBody !== null) {
    return fromBody
  }

  switch (status) {
    case 401:
      return 'Identifiants invalides.'
    case 423:
      return 'Compte temporairement verrouille apres trop de tentatives.'
    case 429:
      return 'Trop de tentatives. Patientez une minute avant de reessayer.'
    default:
      return 'Le service est momentanement indisponible.'
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
  trackSession(response)

  const payload = await readBody(response)
  if (!response.ok) {
    throw new ApiError(messageFor(response.status, payload), response.status, payload, path)
  }

  return payload as T
}

/**
 * Un 400 `antiforgery` (jeton perime apres connexion ou deconnexion) est rejoue
 * une fois. Les autres 400 ne le sont pas : rejouer compterait deux fois un code
 * faux dans le verrouillage.
 */
export async function apiSend<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const send = async (token: string): Promise<Response> =>
    fetch(path, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [CSRF_HEADER]: token,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })

  let response = await send(await ensureCsrfToken())
  trackSession(response)
  let payload = await readBody(response)

  if (response.status === 400 && readString(payload, 'code') === 'antiforgery') {
    resetCsrfToken()
    response = await send(await ensureCsrfToken())
    trackSession(response)
    payload = await readBody(response)
  }

  if (!response.ok) {
    throw new ApiError(messageFor(response.status, payload), response.status, payload, path)
  }

  return payload as T
}
